import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  MessageSquare,
  Image,
  ImagePlus,
  Settings,
  Users,
  Mail,
  Key,
  Briefcase,
  ClipboardList,
  UserCheck,
  BarChart3,
  Inbox,
  Shield,
  Globe,
  Trash2,
  FlaskConical,
  CreditCard,
  ShieldAlert,
  RotateCcw,
  Server,
  ShieldCheck,
  UserCog,
  Building2,
  UserX,
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    label: '',
    items: [
      { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
    ],
  },
  {
    label: 'People',
    items: [
      { name: 'Workday Consultants', href: '/admin/workday-consultants', icon: UserCheck },
      { name: 'Workday Clients', href: '/admin/workday-clients', icon: Briefcase },
      { name: 'Access Submissions', href: '/admin/access', icon: Key },
      { name: 'Invite User Requests', href: '/admin/user-requests', icon: Users },
    ],
  },
  {
    label: 'Jobs & Services',
    items: [
      { name: 'Job Postings', href: '/admin/job-postings', icon: ClipboardList },
      { name: 'Consultant Onboarding', href: '/admin/consultant-onboarding', icon: UserCheck },
      { name: 'PerplexityATS Testing', href: '/admin/ats-testing', icon: FlaskConical },
    ],
  },
  {
    label: 'Communications',
    items: [
      { name: 'Contact Submissions', href: '/admin/contacts', icon: MessageSquare },
      { name: 'Client Messaging', href: '/admin/client-inbox', icon: Inbox },
      { name: 'Consultant Messages', href: '/admin/consultant-messages', icon: MessageSquare },
      { name: 'Community Connect', href: '/admin/community-connect', icon: Globe },
    ],
  },
  {
    label: 'Content & Media',
    items: [
      { name: 'Pages', href: '/admin/pages', icon: FileText },
      { name: 'Media Library', href: '/admin/media', icon: Image },
      { name: 'Logo & Images', href: '/admin/logo-management', icon: ImagePlus },
    ],
  },
  {
    label: 'Analytics & Settings',
    items: [
      { name: 'Website Analytics', href: '/admin/analytics', icon: BarChart3 },
      { name: 'Secure 2FA', href: '/admin/two-factor-auth', icon: Shield },
      { name: 'Account Settings', href: '/admin/account-settings', icon: Settings },
    ],
  },
  {
    label: 'Security & Audit',
    items: [
      { name: 'Security Incidents', href: '/admin/security/incidents', icon: ShieldAlert },
      { name: 'Recovery & Snapshots', href: '/admin/security/recovery', icon: RotateCcw },
      { name: 'Infrastructure Monitor', href: '/admin/security/infrastructure', icon: Server },
      { name: '2FA Audit', href: '/admin/security/2fa-audit', icon: ShieldCheck },
      { name: 'PayPal Audit', href: '/admin/audit/paypal', icon: CreditCard },
      { name: 'Client Audit', href: '/admin/workday/clients/audit', icon: Building2 },
      { name: 'Consultant Audit', href: '/admin/workday/consultants/audit', icon: UserCog },
    ],
  },
];

export default function AdminSidebar() {
  const location = useLocation();
  const [pendingDeletions, setPendingDeletions] = useState(0);

  useEffect(() => {
    const fetch = async () => {
      const { count } = await supabase
        .from('account_deletion_requests')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');
      setPendingDeletions(count || 0);
    };
    fetch();
  }, [location.pathname]);

  const renderItem = (item: NavItem) => {
    const isActive = location.pathname === item.href;
    return (
      <Link
        key={item.href}
        to={item.href}
        className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
          isActive
            ? 'bg-[#40E0D0] bg-opacity-10 text-gray-900 border-l-4 border-[#40E0D0]'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
        }`}
      >
        <item.icon
          className={`mr-3 flex-shrink-0 h-5 w-5 transition-colors ${
            isActive ? 'text-[#40E0D0]' : 'text-gray-400 group-hover:text-[#40E0D0]'
          }`}
        />
        {item.name}
      </Link>
    );
  };

  return (
    <div className="hidden md:flex md:flex-shrink-0">
      <div className="flex flex-col w-64">
        <div className="flex flex-col h-screen bg-white border-r border-gray-200 shadow-sm">
          {/* Brand */}
          <div className="flex items-center flex-shrink-0 px-4 h-16 border-b border-gray-200">
            <span className="text-xl font-bold text-gray-900">Perplexity</span>
          </div>

          <div className="flex-1 flex flex-col overflow-y-auto">
            <nav className="flex-1 px-2 py-4 space-y-0.5">
              {navGroups.map((group) => (
                <div key={group.label || '__root'} className={group.label ? 'pt-4' : ''}>
                  {group.label && (
                    <p className="px-2 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {group.label}
                    </p>
                  )}
                  {group.items.map(renderItem)}
                </div>
              ))}

              {/* Account Deletion — always last, styled red */}
              <div className="pt-4">
                <p className="px-2 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Danger Zone
                </p>
                <Link
                  to="/admin/workday-consultants"
                  className="group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <UserX className="mr-3 flex-shrink-0 h-5 w-5 text-red-500" />
                  <span className="flex-1">Account Deletions</span>
                  {pendingDeletions > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-500 text-white">
                      {pendingDeletions}
                    </span>
                  )}
                </Link>
              </div>
            </nav>
          </div>

          {/* Request Access */}
          <div className="p-4 border-t border-gray-200 mt-auto">
            <Link
              to="/requestaccess"
              className="flex items-center justify-center gap-2 w-full bg-[#40E0D0] bg-opacity-10 hover:bg-opacity-20 px-4 py-2 rounded-lg transition-colors"
            >
              <Mail className="h-5 w-5 text-[#40E0D0]" />
              <span className="font-medium text-gray-900">Request Access</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
